import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { fetchHrGrants, fetchHrPermissions, fetchHrSettings, saveEmploymentType, saveHrGrants, saveHrSetting, saveJobTitle, saveLeaveType, saveSchedule } from "../../api/HrAPIs";
import { fetchAdminUsers } from "../../api/AdminUsersAPIs";
import { Field, HrFrame, HrGuard, inputClass, labelOf, useHr } from "./hrUi";

export default function HrSettingsPage() {
  return <HrGuard anyOf={["HR.ManageSettings", "HR.ManageDepartments"]}><Body /></HrGuard>;
}

function Body() {
  const hr = useHr();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ["hrSettings"], queryFn: fetchHrSettings });
  const catalog = useQuery({ queryKey: ["hrPermissionCatalog"], queryFn: fetchHrPermissions, enabled: hr.owner });
  const users = useQuery({ queryKey: ["adminUsers"], queryFn: fetchAdminUsers, enabled: hr.owner });
  const [userId, setUserId] = useState("");
  const [grants, setGrants] = useState([]);
  const [title, setTitle] = useState("");
  const [policy, setPolicy] = useState("");

  const toggle = (permission, scope) => {
    setGrants((current) => {
      const exists = current.some((grant) => grant.permission === permission && grant.scope === scope);
      return exists ? current.filter((grant) => !(grant.permission === permission && grant.scope === scope)) : [...current, { permission, scope }];
    });
  };

  return (
    <HrFrame title="إعدادات الموارد البشرية" subtitle="القواعد القابلة للتغيير تُحفظ هنا. حساب الراتب وطرق التعويض تبقى ثلاث صيغ معروفة حتى لا يُحسب مبلغ بطريقة غير مختبرة.">
      <form className="mb-4 flex gap-2" onSubmit={async (event) => { event.preventDefault(); try { await saveJobTitle({ name: title, isActive: true }); setTitle(""); queryClient.invalidateQueries({ queryKey: ["hrSettings"] }); } catch (error) { toast.error(error.message); } }}>
        <input className={inputClass} placeholder="مسمى وظيفي جديد" value={title} onChange={(event) => setTitle(event.target.value)} />
        <button className="rounded-xl bg-indigo-600 px-4 py-2 text-sm text-white">إضافة</button>
      </form>
      <div className="mb-6 grid gap-2">
        {(settings.data?.jobTitles || []).map((item) => <p key={item.id} className="text-sm">{item.name}</p>)}
      </div>
      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <Mini title="أنواع التوظيف" rows={settings.data?.employmentTypes} onAdd={(name, code) => saveEmploymentType({ name, code, isActive: true })} />
        <Mini title="أنواع الإجازات" rows={settings.data?.leaveTypes} onAdd={(name, code) => saveLeaveType({ name, code, isPaid: code !== "unpaid", isActive: true })} />
      </div>
      <form className="mb-6 grid gap-2 rounded-2xl border bg-white p-4 md:grid-cols-2" onSubmit={async (event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        try {
          await saveSchedule({ name: data.get("name"), startTime: data.get("start"), endTime: data.get("end"), graceMinutes: Number(data.get("grace")), weeklyHolidays: data.get("holidays"), isActive: true });
          toast.success("تم حفظ الدوام");
          queryClient.invalidateQueries({ queryKey: ["hrSettings"] });
        } catch (error) { toast.error(error.message); }
      }}>
        <h2 className="font-bold md:col-span-2">دوام</h2>
        <Field label="الاسم"><input name="name" className={inputClass} required /></Field>
        <Field label="من"><input name="start" className={inputClass} defaultValue="09:00" /></Field>
        <Field label="إلى"><input name="end" className={inputClass} defaultValue="17:00" /></Field>
        <Field label="سماح التأخير بالدقائق"><input name="grace" className={inputClass} type="number" defaultValue="10" /></Field>
        <Field label="عطلة أسبوعية (0 أحد … 6 سبت)"><input name="holidays" className={inputClass} defaultValue="5,6" /></Field>
        <button className="rounded-xl bg-slate-900 px-4 py-2 text-sm text-white">حفظ الدوام</button>
      </form>
      {hr.can("HR.ManageSettings") && (
        <form className="mb-6 grid gap-2" onSubmit={async (event) => {
          event.preventDefault();
          try { await saveHrSetting({ key: "attendance.deductionPolicy", valueJson: policy }); toast.success("تم حفظ سياسة الخصم"); }
          catch (error) { toast.error(error.message); }
        }}>
          <Field label="سياسة خصم الحضور JSON. لا يُخصم شيء إلا إذا كان enabled و approved كلاهما true.">
            <textarea className={inputClass} rows={4} value={policy} placeholder='{"enabled":false,"approved":false,"absenceAmount":0,"lateAmount":0,"deductUnpaidLeave":false}' onChange={(event) => setPolicy(event.target.value)} />
          </Field>
          <button className="rounded-xl border px-4 py-2 text-sm">حفظ السياسة</button>
        </form>
      )}
      {hr.owner && (
        <section>
          <h2 className="mb-2 font-bold">صلاحيات الموارد البشرية</h2>
          <p className="mb-2 text-sm text-slate-500">المدير العادي لا يملك هذه الصلاحيات تلقائياً. المالك يمنحها، ولا تُغيّر أدوار الطلبات الحالية.</p>
          <Field label="الحساب">
            <select className={inputClass} value={userId} onChange={async (event) => {
              const next = event.target.value;
              setUserId(next);
              if (!next) { setGrants([]); return; }
              try { setGrants(await fetchHrGrants(next)); }
              catch (error) { toast.error(error.message); }
            }}>
              <option value="">اختر</option>
              {(users.data || []).map((user) => <option key={user.id} value={user.id}>{user.email} · {user.role}</option>)}
            </select>
          </Field>
          <div className="mt-3 max-h-80 overflow-auto rounded-2xl border bg-white p-3">
            {(catalog.data?.permissions || []).map((permission) => (
              <div key={permission.code} className="border-b border-slate-100 py-2">
                <p className="text-sm font-semibold">{permission.label}</p>
                <div className="mt-1 flex flex-wrap gap-2 text-xs">
                  {(catalog.data?.scopes || []).map((scope) => (
                    <label key={scope} className="flex items-center gap-1">
                      <input type="checkbox" checked={grants.some((grant) => grant.permission === permission.code && grant.scope === scope)} onChange={() => toggle(permission.code, scope)} />
                      {labelOf(scope)}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button type="button" className="mt-3 rounded-xl bg-indigo-600 px-4 py-2 text-sm text-white" disabled={!userId} onClick={async () => {
            try { await saveHrGrants({ userId: Number(userId), grants }); toast.success("تم تحديث الصلاحيات"); queryClient.invalidateQueries({ queryKey: ["hrAccess"] }); }
            catch (error) { toast.error(error.message); }
          }}>حفظ الصلاحيات</button>
        </section>
      )}
    </HrFrame>
  );
}

function Mini({ title, rows, onAdd }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  return (
    <form className="rounded-2xl border bg-white p-4" onSubmit={async (event) => {
      event.preventDefault();
      try { await onAdd(name, code); setName(""); setCode(""); queryClient.invalidateQueries({ queryKey: ["hrSettings"] }); }
      catch (error) { toast.error(error.message); }
    }}>
      <h2 className="mb-2 font-bold">{title}</h2>
      {(rows || []).map((row) => <p key={row.id} className="text-sm">{row.name}</p>)}
      <input className={`${inputClass} mt-2`} placeholder="الاسم" value={name} onChange={(event) => setName(event.target.value)} />
      <input className={`${inputClass} mt-2`} placeholder="الكود" value={code} onChange={(event) => setCode(event.target.value)} />
      <button className="mt-2 text-sm text-indigo-600">إضافة</button>
    </form>
  );
}
